// SelectionTransformCommand — Photoshop's Select > Transform Selection.
//
// Applies an affine transform (translate, scale, rotate around the selection
// bounding-box center) to the current document selection mask. The layer
// pixels are not affected; only the mask is transformed.
//
// Also exposes the pure transform functions used by:
//   - The interactive "Transform Selection" UI in main.ts (still uses its own
//     local copy at the moment; this module is the canonical home).
//   - ResizeImageCommand / RotateImageCommand / ResizeCanvasCommand for
//     keeping doc.selection in sync with canvas geometry changes (#69).
//
// Layer: 3 (Document Model). MUST remain DOM-free / browser-free.

import type { Document } from "../document/Document";
import { Selection } from "../selection/Selection";
import type { Command } from "./Command";
import { SetSelectionCommand } from "./SetSelectionCommand";

export interface SelectionAffine {
  /** Translation in pixels. */
  translateX: number;
  translateY: number;
  /** Scale factors around the selection bbox center. */
  scaleX: number;
  scaleY: number;
  /** Rotation in radians around the bbox center. */
  rotation: number;
}

/**
 * Transform a selection by translate / scale / rotate around its current
 * bounding-box center. Output mask has the same docW/docH as the input.
 *
 * Uses inverse-mapping with nearest-neighbor sampling — fast and lossless for
 * binary masks, acceptable for anti-aliased ones (slightly stair-stepped
 * boundaries at low scales).
 */
export function transformSelectionMask(
  sel: Selection,
  affine: SelectionAffine,
): Selection {
  const { scaleX, scaleY, rotation, translateX, translateY } = affine;
  const w = sel.docW;
  const h = sel.docH;
  if (sel.isEmpty()) return Selection.empty(w, h);
  const b = sel.bounds();
  if (!b) return Selection.empty(w, h);

  const cx = b.x + b.w / 2;
  const cy = b.y + b.h / 2;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const out = new Uint8Array(w * h);
  const sx = scaleX || 1;
  const sy = scaleY || 1;

  for (let oy = 0; oy < h; oy++) {
    for (let ox = 0; ox < w; ox++) {
      // Inverse transform: dest → src.
      const dx = ox - cx - translateX;
      const dy = oy - cy - translateY;
      let rx = dx * cos + dy * sin;
      let ry = -dx * sin + dy * cos;
      rx /= sx;
      ry /= sy;
      const sX = Math.round(rx + cx);
      const sY = Math.round(ry + cy);
      if (sX >= 0 && sX < w && sY >= 0 && sY < h) {
        out[oy * w + ox] = sel.mask[sY * w + sX] ?? 0;
      }
    }
  }
  return new Selection(w, h, out);
}

/**
 * Scale a selection mask to a new doc size. Used by ResizeImageCommand so the
 * selection follows the canvas resize. Uses nearest-neighbor sampling.
 *
 * `sel` is sized to (oldW, oldH). The result is sized to (newW, newH).
 */
export function scaleSelectionToNewSize(
  sel: Selection | null,
  newW: number,
  newH: number,
): Selection | null {
  if (!sel) return null;
  if (sel.isEmpty()) return Selection.empty(newW, newH);
  const oldW = sel.docW;
  const oldH = sel.docH;
  if (oldW === newW && oldH === newH) return sel.clone();
  if (newW <= 0 || newH <= 0) return Selection.empty(Math.max(0, newW), Math.max(0, newH));

  const out = new Uint8Array(newW * newH);
  const ratioX = oldW / newW;
  const ratioY = oldH / newH;
  for (let y = 0; y < newH; y++) {
    const sy = Math.min(oldH - 1, Math.max(0, Math.round(y * ratioY)));
    for (let x = 0; x < newW; x++) {
      const sx = Math.min(oldW - 1, Math.max(0, Math.round(x * ratioX)));
      out[y * newW + x] = sel.mask[sy * oldW + sx] ?? 0;
    }
  }
  return new Selection(newW, newH, out);
}

/**
 * Rotate a selection mask 90° clockwise. New doc size = (oldH, oldW).
 */
export function rotateSelection90CW(sel: Selection | null): Selection | null {
  if (!sel) return null;
  const oldW = sel.docW;
  const oldH = sel.docH;
  if (sel.isEmpty()) return Selection.empty(oldH, oldW);
  const out = new Uint8Array(oldW * oldH);
  for (let y = 0; y < oldH; y++) {
    for (let x = 0; x < oldW; x++) {
      const v = sel.mask[y * oldW + x] ?? 0;
      if (v === 0) continue;
      // (x, y) → (oldH - 1 - y, x) in the new (oldH × oldW) canvas.
      const nx = oldH - 1 - y;
      const ny = x;
      out[ny * oldH + nx] = v;
    }
  }
  return new Selection(oldH, oldW, out);
}

/**
 * Rotate a selection mask 90° counter-clockwise. New doc size = (oldH, oldW).
 */
export function rotateSelection90CCW(sel: Selection | null): Selection | null {
  if (!sel) return null;
  const oldW = sel.docW;
  const oldH = sel.docH;
  if (sel.isEmpty()) return Selection.empty(oldH, oldW);
  const out = new Uint8Array(oldW * oldH);
  for (let y = 0; y < oldH; y++) {
    for (let x = 0; x < oldW; x++) {
      const v = sel.mask[y * oldW + x] ?? 0;
      if (v === 0) continue;
      // (x, y) → (y, oldW - 1 - x) in the new (oldH × oldW) canvas.
      const nx = y;
      const ny = oldW - 1 - x;
      out[ny * oldH + nx] = v;
    }
  }
  return new Selection(oldH, oldW, out);
}

/**
 * Rotate a selection mask 180°. Same doc dimensions.
 */
export function rotateSelection180(sel: Selection | null): Selection | null {
  if (!sel) return null;
  const w = sel.docW;
  const h = sel.docH;
  if (sel.isEmpty()) return Selection.empty(w, h);
  const out = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      out[(h - 1 - y) * w + (w - 1 - x)] = sel.mask[y * w + x] ?? 0;
    }
  }
  return new Selection(w, h, out);
}

/** Mirror horizontal (flip across vertical axis). */
export function flipSelectionH(sel: Selection | null): Selection | null {
  if (!sel) return null;
  const w = sel.docW;
  const h = sel.docH;
  if (sel.isEmpty()) return Selection.empty(w, h);
  const out = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      out[y * w + (w - 1 - x)] = sel.mask[y * w + x] ?? 0;
    }
  }
  return new Selection(w, h, out);
}

/** Mirror vertical (flip across horizontal axis). */
export function flipSelectionV(sel: Selection | null): Selection | null {
  if (!sel) return null;
  const w = sel.docW;
  const h = sel.docH;
  if (sel.isEmpty()) return Selection.empty(w, h);
  const out = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      out[(h - 1 - y) * w + x] = sel.mask[y * w + x] ?? 0;
    }
  }
  return new Selection(w, h, out);
}

/**
 * Translate a selection mask within the same-sized doc.
 * Pixels that move off-canvas are clipped.
 */
export function translateSelection(sel: Selection | null, dx: number, dy: number): Selection | null {
  if (!sel) return null;
  return sel.translate(dx, dy);
}

/**
 * Command wrapper. Stores the (next) transformed selection at construction
 * time so the inverse just restores the prior selection via SetSelectionCommand.
 */
export class SelectionTransformCommand implements Command {
  readonly kind = "selection-transform";
  private inner: SetSelectionCommand | null = null;

  constructor(private readonly affine: SelectionAffine) {}

  apply(doc: Document): Document {
    if (!doc.selection) return doc;
    const next = transformSelectionMask(doc.selection, this.affine);
    this.inner = new SetSelectionCommand(next);
    return this.inner.apply(doc);
  }

  invert(before: Document, after: Document): Command {
    if (!this.inner) return new SetSelectionCommand(before.selection);
    return this.inner.invert(before, after);
  }
}
